import React from 'react'
import axios from 'axios'

import {
  useEnterpriseContext,
  useHorizonSessionSettings,
  useLogger,
} from '@thg-commerce/enterprise-core'
import { pushToEventGA4 } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import { SelectedOptions } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import { SwatchShape } from '@thg-commerce/gravity-elements/Swatch/types'

import { ProductOptions } from '../components/productOptions'
import { SSMProductInfo } from '../components/ShoeSizeMe/ShoeSizeMeLoaderScript'
import { ShoeSizeMePDP } from '../components/ShoeSizeMe/ShoeSizeMePDP'
import { Variant } from '../index'
import { TransformedOption } from '../transformers/option'
import {
  ChoiceSelectedVariantMap,
  ProductOptionsMap,
  UNSELECTED_KEY,
} from '../transformers/productOptionsMap'
import { VariantOptions } from '../types'
import { findProductOptions } from '../utils/options'

type ProductOptionRendererProps = {
  updateSelectedVariant: (variant: Variant) => void
  isVariantsUpdatedForGA4: boolean
  updateVariantsForGA4: (updateVariantsForGA4: boolean) => void
  options: TransformedOption[]
  setOptionsCallback: (options: TransformedOption[]) => void
  selectedOptions: SelectedOptions
  setSelectedOptionsCallback: (selectedOptions: SelectedOptions) => void
  masterProductSku: number
  sortedOptionKeys: string[]
  queryFullVariantData: boolean
  setDisplayError: (setError: boolean) => void
  displayError: boolean
  onDropdownClick: () => void
  onColourSwatchClick: () => void
  onLinkedSkuOptionChange: (option: string) => void
  preselectVariant: boolean
  productDataForGA4EnhancedEcom?: {
    index: number
    item_id: string
    item_external_id: string
    item_name: string
    price?: string
    item_brand: any
    item_category?: string
    item_variant: string
    discount: string
    item_currency: string
    item_page: string
    quantity: number
    item_list_name?: string
  }
  forceDropDowns?: boolean
  swatchShape?: SwatchShape
  showSwatches?: boolean
  notifyWhenInStock?: boolean
  labelCustomComponent?: React.ReactNode
  isFirstProductRecommendation?: boolean
  linkedOn?: string | null
  disableColorDropdown?: boolean
  showVariantNameNextTocolorLabel?: boolean
  setPersonalisationImages?: (
    personalisationImages: ChoiceSelectedVariantMap | VariantOptions,
  ) => void
  setChoiceMapLoading: (boolean: boolean) => void
  shoeSizeMeEnabled?: boolean
  SSMProduct: SSMProductInfo
  hideOptionFeatures?: boolean
  enableFBTProductOptionPlacement?: boolean
  choiceMapLoading?: boolean
}

const DEFAULT_TIMEOUT = 10000

export const ProductOptionRenderer: (
  props: ProductOptionRendererProps,
) => JSX.Element = (props) => {
  const {
    productDataForGA4EnhancedEcom,
    isVariantsUpdatedForGA4,
    updateVariantsForGA4,
  } = props

  const { currentLocation, config } = useEnterpriseContext()

  const choicesToOptionsMap = React.useRef<ProductOptionsMap>({})
  const logger = useLogger()
  const sessionSettings = useHorizonSessionSettings()

  const timeout = config?.PRODUCT_OPTIONS_TIMEOUT
    ? config.PRODUCT_OPTIONS_TIMEOUT
    : DEFAULT_TIMEOUT

  // @TODO: REBUILD-8643 replace useEffect with webworker
  React.useEffect(() => {
    const productOptionsEndpoint = props.queryFullVariantData
      ? `/e2/operation/product/product-options/`
      : `/e2/operation/product-options/`
    axios
      .get(`${productOptionsEndpoint}${props.masterProductSku}`, {
        timeout,
        headers: {
          'x-customer-location': currentLocation,
        },
      })
      .then((response) => {
        choicesToOptionsMap.current = response.data
        props.setChoiceMapLoading(false)
      })
      .catch((e) => {
        logger.error(`Failed to load product options with error ${e.message}`),
          console.trace(e)
      })
  }, [
    currentLocation,
    logger,
    props.masterProductSku,
    props.queryFullVariantData,
    timeout,
  ])

  React.useEffect(() => {
    props.setChoiceMapLoading(
      Object.keys(choicesToOptionsMap.current).length === 0,
    )
  }, [
    props.setChoiceMapLoading,
    Object.keys(choicesToOptionsMap.current).length,
  ])

  const imageData = choicesToOptionsMap.current['images']

  props.setPersonalisationImages?.(imageData)

  React.useEffect(() => {
    if (productDataForGA4EnhancedEcom && isVariantsUpdatedForGA4) {
      const {
        index,
        item_name,
        item_id,
        item_external_id,
        price,
        item_brand,
        item_category,
        item_variant,
        discount,
        item_currency,
        item_page,
        quantity,
        item_list_name,
      } = productDataForGA4EnhancedEcom
      pushToEventGA4({
        event: 'ecom_event',
        event_name: 'view_item',
        ecommerce: {
          currencyCode: sessionSettings.currency,
          items: [
            {
              index,
              item_name,
              item_id,
              price,
              item_brand,
              item_category,
              item_variant,
              discount,
              item_external_id,
              item_page,
              item_currency,
              quantity,
              item_list_name,
            },
          ],
        },
      })
      updateVariantsForGA4(false)
    }
  }, [
    productDataForGA4EnhancedEcom,
    isVariantsUpdatedForGA4,
    updateVariantsForGA4,
    sessionSettings.currency,
  ])

  const onOptionChange = (optionKey?: string, value?: string) => {
    if (!optionKey || !value || props.choiceMapLoading) {
      return
    }

    if (props.selectedOptions[optionKey] === value) {
      return
    }

    const updatedSelectedOptions = props.selectedOptions
    updatedSelectedOptions[optionKey] = value

    props.setSelectedOptionsCallback(updatedSelectedOptions)

    const productOptions = findProductOptions(
      props.sortedOptionKeys,
      props.selectedOptions,
      choicesToOptionsMap.current,
    )

    if (!productOptions) {
      if (props.linkedOn) {
        const fallbackSelectedOptions = Object.entries(
          updatedSelectedOptions,
        ).reduce((accumulator, [optionKey, choiceKey]) => {
          if (optionKey === props.linkedOn) {
            accumulator[optionKey] = choiceKey
          } else {
            accumulator[optionKey] = UNSELECTED_KEY
          }
          return accumulator
        }, {})

        props.setSelectedOptionsCallback(fallbackSelectedOptions)
        const fallbackOptions = findProductOptions(
          props.sortedOptionKeys,
          fallbackSelectedOptions,
          choicesToOptionsMap.current,
        )

        if (fallbackOptions) {
          props.updateSelectedVariant(fallbackOptions.variant)
          props.setOptionsCallback(fallbackOptions.options)
        }
      }
      return
    }

    props.updateSelectedVariant(productOptions.variant)
    props.setOptionsCallback(productOptions.options)
  }

  return (
    <React.Fragment>
      {props.shoeSizeMeEnabled && !props.hideOptionFeatures && (
        <ShoeSizeMePDP
          optionsMap={choicesToOptionsMap}
          product={props.SSMProduct}
        />
      )}
      <ProductOptions
        options={props.options}
        displayError={props.displayError}
        setDisplayError={props.setDisplayError}
        forceDropDowns={props.forceDropDowns}
        swatchShape={props.swatchShape}
        showSwatches={props.showSwatches}
        labelCustomComponent={props.labelCustomComponent}
        isFirstProductRecommendation={props.isFirstProductRecommendation}
        notifyWhenInStock={props.notifyWhenInStock}
        selectedOptions={props.selectedOptions}
        onOptionChange={onOptionChange}
        onDropdownClick={props.onDropdownClick}
        onLinkedSkuOptionChange={props.onLinkedSkuOptionChange}
        onColourSwatchClick={props.onColourSwatchClick}
        preselectVariant={props.preselectVariant}
        disableColorDropdown={props.disableColorDropdown}
        showVariantNameNextTocolorLabel={props.showVariantNameNextTocolorLabel}
        enableFBTProductOptionPlacement={props.enableFBTProductOptionPlacement}
        choiceMapLoading={props.choiceMapLoading}
      />
    </React.Fragment>
  )
}
